/* eslint-disable @next/next/no-img-element */
//components
import { Container } from '@/components/Grid';
import {
  Kicker,
  KickerDot,
  SectionTitle,
  SectionLede,
  Grad,
} from '@/containers-ai/SectionHeading';

import { FreightExperts } from '@/containers-ai/FreightExperts';
import { PillLink } from '@/containers-ai/Buttons';
import { CountUp } from '@/containers-ai/CountUp';

import {
  PlatformScale,
  platformScaleStats,
} from '@/containers-ai/PlatformScale';

//styles
import {
  LiveSection,
  BigProof,
  BigProofValue,
  BigProofText,
  TaskGrid,
  TaskCard,
  TaskTitle,
  TaskDescription,
  TaskMetricWrap,
  TaskMetricText,
  TaskMetric,
  TaskMetricLabel,
  TaskCustomerBox,
  TaskCustomerLogo,
  CustomCard,
  CustomCardKicker,
  CustomCardTitle,
  CustomCardCopy,
  PlusDivider,
} from './LiveInProduction.styles';

const tasks = [
  {
    slug: 'document-collection-and-filing',
    title: 'Document collection & filing',
    description: 'Automate collection of PoDs, Invoices and other documents.',
    metric: '70–90%',
    label: 'less document-handling time',
    customer: null,
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & status updates',
    description:
      'We pull status from carriers and push clean updates, so your team stops making check calls.',
    metric: '5–10',
    label: 'minutes saved per tracked load',
    customer: null,
  },
  {
    slug: 'tender-failures',
    title: 'Tender failures',
    description:
      'We re-tender down your routing guide and re-book before the miss ever reaches your team.',
    metric: '8–10%',
    label: 'overpayment on flat rates eliminated',
    customer: null,
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive load audits',
    description:
      'We check every load before pickup and flag at-risk transit before it becomes a penalty.',
    metric: '30–40%',
    label: 'reduction in avoidable penalty charges',
    customer: null,
  },
  {
    slug: 'claims-processing',
    title: 'Claims processing',
    description:
      'We gather the docs, validate the claim, and draft the response for your approval.',
    metric: '2x',
    label: 'faster claims recovery with higher filing rates',
    customer: null,
  },
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & re-scheduling',
    description:
      'We book dock appointments, and when a pickup slips, re-schedule and notify everyone.',
    metric: '5–10',
    label: 'minutes saved per appointment',
    customer: null,
  },
];

export function LiveInProduction() {
  return (
    <LiveSection id="ai">
      <Container>
        <Kicker>
          <KickerDot />
          What we automate
        </Kicker>
        <SectionTitle style={{ maxWidth: '905px' }}>
          The repetitive work that eats your team&rsquo;s day. <br />
          <Grad>We take it off your plate.</Grad>
        </SectionTitle>
        <SectionLede>
          Every shipper runs the same manual tasks over and over. Loadsmart AI
          automates the tasks, end-to-end.
        </SectionLede>

        <BigProof>
          <BigProofValue>
            <CountUp value={3000} />+
          </BigProofValue>
          <BigProofText>
            real TMS objects <b>orchestrated in production by Loadsmart AI</b>
          </BigProofText>
        </BigProof>

        <TaskGrid>
          {tasks.map((task) => (
            <TaskCard key={task.title} href={`/ai/${task.slug}/`}>
              <TaskTitle>{task.title}</TaskTitle>
              <TaskDescription>{task.description}</TaskDescription>
              <TaskMetricWrap>
                <TaskMetricText>
                  <TaskMetric>{task.metric}</TaskMetric>
                  <TaskMetricLabel>{task.label}</TaskMetricLabel>
                </TaskMetricText>
                {task.customer && (
                  <TaskCustomerBox>
                    <TaskCustomerLogo
                      src={task.customer.img}
                      alt={task.customer.alt}
                    />
                  </TaskCustomerBox>
                )}
              </TaskMetricWrap>
            </TaskCard>
          ))}
        </TaskGrid>

        <CustomCard>
          <div>
            <CustomCardKicker>Custom &mdash; anything else</CustomCardKicker>
            <CustomCardTitle>
              If you can describe the task, we&rsquo;ll build the agent to run
              it.
            </CustomCardTitle>
            <CustomCardCopy>
              Bring the repetitive, rules-based work that eats your team’s day
              and we’ll build a custom agent for it.
            </CustomCardCopy>
          </div>
          <PillLink href="/contact/#contact-form" className="qlf-btn-2">
            Book a working session &#8594;
          </PillLink>
        </CustomCard>

        <PlusDivider aria-hidden="true">+</PlusDivider>

        <FreightExperts border={false} as="div" />

        <PlatformScale stats={platformScaleStats} />
      </Container>
    </LiveSection>
  );
}
